/**
 * THE ADDRESS OF THE MANUAL SIDE, FOR ONE PARTICULAR CAR.
 *
 * `/manuals` has read `?vin=` and `?year=&make=&model=` off its own query
 * string since the day it was written, and the comment there says exactly
 * why: "the VIN is already in the CRM. Making him read it off one page and
 * type it into another is how the manual side goes unused."
 *
 * Nothing in the desk has ever built that link. Manuals was reachable from
 * the phone's profile menu and from the More page, both of which open the
 * shelf empty and ask you to type in a car you are already looking at.
 *
 * Anthony, 2026-09-20: "I still don't know where you put the manual links
 * on jobs." He could not find them because they were not there.
 *
 * ─── THE VIN WINS, AND THE PAGE AGREES ────────────────────────────────
 *
 * The manuals page prefers the VIN when both are present, because it
 * decodes to the exact engine and trim and year/make/model is the same car
 * with the useful part missing. This builds one or the other rather than
 * both, so the two halves cannot disagree about which was meant.
 *
 * Eleven characters is the shortest thing the decoder will attempt, which
 * is the same floor the page itself uses. Below that, fall back to the
 * three fields; below those, the bare shelf, because a link that opens the
 * manuals side is still better than no link at all.
 */

export type ManualCar = {
  vin?: string | null;
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
};

export function manualsHref(v: ManualCar | null | undefined): string {
  const vin = (v?.vin ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (vin.length >= 11) return `/manuals?vin=${encodeURIComponent(vin)}`;

  const year = String(v?.year ?? "").trim();
  const make = (v?.make ?? "").trim();
  const model = (v?.model ?? "").trim();
  if (year && make && model) {
    return "/manuals?" + new URLSearchParams({ year, make, model }).toString();
  }
  return "/manuals";
}

/**
 * What the link should say it will do, which is not the same sentence for
 * a car we can identify and one we cannot. Promising "recalls and wiring
 * diagrams for this truck" and then opening an empty shelf is the shape of
 * broken trust this codebase keeps having to undo.
 */
export function manualsBlurb(v: ManualCar | null | undefined, thing: string): string {
  const href = manualsHref(v);
  if (href.includes("vin=")) {
    return `Recalls, wiring diagrams and factory portals, on this VIN`;
  }
  if (href.includes("year=")) {
    return `Recalls, wiring diagrams and factory portals for this ${thing}`;
  }
  return `Recalls, wiring diagrams and factory portals. Add a VIN and this opens on the ${thing} itself`;
}
